
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Team {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TeamInvitation {
  id: string;
  team_id: string;
  inviter_id: string;
  invitee_id: string;
  status: 'pending' | 'accepted' | 'declined';
  message?: string;
  created_at: string;
  updated_at: string;
}

export const useTeams = ({ enabled = false } = {}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchUserTeams = async () => {
    if (!enabled) {
      setTeams([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setTeams([]);
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching teams for user:', user.id);

      // Check if teams table exists first by querying with limit 0
      const { error: tableCheckError } = await supabase
        .from('teams')
        .select('id')
        .limit(0);

      // If table doesn't exist (42P01) or permission denied (42501), silently return empty
      if (tableCheckError && (tableCheckError.code === '42P01' || tableCheckError.code === '42501')) {
        console.log('📋 Teams table not available - teams functionality disabled');
        setTeams([]);
        setLoading(false);
        return;
      }

      // Fetch teams using the new RLS policy that handles both created and member teams
      const { data: userTeams, error: teamsError } = await supabase
        .from('teams')
        .select('id, name, description, created_by, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(50);

      if (teamsError) {
        // Don't log permission errors for teams table - it's optional functionality
        if (teamsError.code !== '42501') {
          console.error('❌ Error fetching teams:', teamsError);
        }
        setError(teamsError.code === '42501' ? null : teamsError.message);
        setTeams([]);
        return;
      }

      console.log('✅ Successfully fetched teams:', userTeams);
      setTeams(userTeams || []);
      
    } catch (err) {
      console.error('❌ Error in fetchUserTeams:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (name: string, description?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to create a team",
          variant: "destructive"
        });
        return null;
      }

      // Check if teams table exists first
      const { error: tableCheckError } = await supabase
        .from('teams')
        .select('id')
        .limit(0);

      if (tableCheckError && (tableCheckError.code === '42P01' || tableCheckError.code === '42501')) {
        toast({
          title: "Teams not available",
          description: "Team functionality is not available in this application",
          variant: "destructive"
        });
        return null;
      }

      // Check if user has a profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Error checking user profile:', profileError);
        toast({
          title: "Error",
          description: "Failed to verify user profile",
          variant: "destructive"
        });
        return null;
      }

      if (!userProfile) {
        toast({
          title: "Profile required",
          description: "Please complete your profile setup before creating a team",
          variant: "destructive"
        });
        return null;
      }

      console.log('🚀 Creating team:', name, 'for user:', user.id);

      const { data, error } = await supabase
        .from('teams')
        .insert({
          name: name.trim(),
          description: description?.trim() || null,
          created_by: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating team:', error);
        throw error;
      }

      console.log('✅ Team created successfully:', data);
      
      toast({
        title: "Team created",
        description: `"${name}" has been created successfully!`
      });

      // Refresh teams list
      await fetchUserTeams();
      return data;

    } catch (error: any) {
      console.error('❌ Error creating team:', error);
      toast({
        title: "Failed to create team",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  const inviteToTeam = async (teamId: string, inviteeUserId: string, message?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ No authenticated user');
        toast({
          title: "Authentication required",
          description: "Please sign in to send invitations",
          variant: "destructive"
        });
        return null;
      }

      console.log('🔄 Starting invitation process...');
      console.log('👤 Current user:', user.id);
      console.log('🎯 Target user:', inviteeUserId);
      console.log('🏢 Team ID:', teamId);

      // Check if user has any teams first
      if (teams.length === 0) {
        console.error('❌ User has no teams');
        toast({
          title: "Create a team first",
          description: "You need to create a team before you can invite members",
          variant: "destructive"
        });
        return null;
      }

      // Don't invite if it's the current user's own post/cast
      if (user.id === inviteeUserId) {
        console.log('👤 Cannot invite yourself to a team');
        toast({
          title: "Cannot invite yourself",
          description: "You cannot invite yourself to a team",
          variant: "destructive"
        });
        return null;
      }

      // Get the team name for the invitation message
      const team = teams.find(t => t.id === teamId);
      if (!team) {
        console.error('❌ Team not found:', teamId);
        toast({
          title: "Team not found",
          description: "The selected team could not be found",
          variant: "destructive"
        });
        return null;
      }

      console.log('📧 Sending invitation to team:', teamId, 'for user:', inviteeUserId);

      // Create the invitation message
      const invitationMessage = message || `You've been invited to join the "${team.name}" farming team! Would you like to collaborate with us?`;
      
      // First, create the team invitation record
      console.log('📝 Creating team invitation record');
      const { data: invitationData, error: invitationError } = await supabase
        .from('team_invitations')
        .insert({
          team_id: teamId,
          inviter_id: user.id,
          invitee_id: inviteeUserId,
          message: invitationMessage,
          status: 'pending'
        })
        .select()
        .single();

      if (invitationError) {
        console.error('❌ Error creating team invitation:', invitationError);
        toast({
          title: "Failed to create invitation",
          description: invitationError.message,
          variant: "destructive"
        });
        return null;
      }

      console.log('✅ Team invitation created successfully:', invitationData);

      // Then, send the invitation as an in-app message
      console.log('💬 Sending message to user:', inviteeUserId);
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: inviteeUserId,
          content: invitationMessage
        });

      if (messageError) {
        console.error('❌ Error sending invitation message:', messageError);
        // Don't fail the whole process if message fails, invitation is already created
        console.log('⚠️ Invitation created but message failed to send');
      } else {
        console.log('✅ Message sent successfully');
      }

      toast({
        title: "Invitation sent",
        description: `The farmer has been invited to join "${team.name}"`
      });

      return invitationData;
    } catch (err) {
      console.error('❌ Error sending invitation:', err);
      toast({
        title: "Failed to send invitation",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchUserTeams();
    }
  }, [enabled]);

  return {
    teams,
    loading,
    error,
    fetchUserTeams,
    createTeam,
    inviteToTeam,
    hasTeams: teams.length > 0
  };
};
